import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { InvUploadColumnName, InvUploadHeaderColumn, DateRangeEvent, GetInvUploadRequest, PagedInvUploadResponse, InvUploadHeaderResponse, InvUploadStatusEnum, DownloadInvUploadDetailRequest, InvUploadHeaderRowData } from './models/inv-upload.model';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { InvUploadService } from '../../service/inv-upload.service';
import { PageInfo } from 'src/app/core/models/page-info';
import { finalize } from 'rxjs';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { HttpErrorResponse } from '@angular/common/http';
import { FileDownloadUtil } from 'src/app/core/utils/file-download.util';
import { ExcelFile } from 'src/app/core/models/excel-file';
import { InvUploadMapperService } from '../../service/inv-upload-mapper.service';

@Component({
  selector: 'laas-inv-upload-component',
  templateUrl: './inv-upload-component.component.html',
  styleUrl: './inv-upload-component.component.scss',
})
export class InvUploadMaintenanceComponent extends SessionBaseComponent implements OnInit {

  // Expose enum to template
  InvUploadColumnName = InvUploadColumnName;

  readonly preferenceKey: string = UserPreferenceConstants.InvUploadHeader;

  /**
   * Statuses that allow downloading detail excel
   * Single source of truth for valid download states
   */
  private readonly DOWNLOADABLE_STATUSES: Set<InvUploadStatusEnum> = new Set([
    InvUploadStatusEnum.MANAGE_FINISHED,
    InvUploadStatusEnum.MANAGE_ERROR,
    InvUploadStatusEnum.RESTORATION_ERROR,
    InvUploadStatusEnum.RESTORATION_FINISHED
  ]);

  invUploadHeaderColumns: InvUploadHeaderColumn[];
  searchForm!: FormGroup<{
    createDateStart: FormControl<string | null>;
    createDateEnd: FormControl<string | null>;
  }>;

  isLoading = false;
  isLoadingDetailExcel: Map<string, boolean> = new Map();
  isLoadingExampleExcel = false;

  @ViewChild('uploadDate') uploadDate!: CalendarComponent;
  @ViewChild("invUploadResultTable") table!: DataTableComponent;

  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };

  readonly manageInvUploadPermissionCode: PermissionConstants = PermissionConstants.ManageInvUpload;
  currentLang: string = this.laasSetting.language;
  

  searchResults: InvUploadHeaderRowData[] = [];
  selectedFile: File | null = null;
  sortOrder: SortOrder = {
    property: InvUploadColumnName.CREATED_AT,
    direction: -1
  };

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private invUploadService: InvUploadService,
    private invUploadMapper: InvUploadMapperService
  ) {
    super(injector);
    this.invUploadHeaderColumns = [
      { field: InvUploadColumnName.INV_UPLOAD_NO, header: 'InvUploadMaintenance.Column.invUploadNo', sort: 'true' ,type: 'text'},
      { field: InvUploadColumnName.DOWNLOAD_DETAIL, header: 'InvUploadMaintenance.Column.downloadDetail', sort: 'false', type: 'button' },
      { field: InvUploadColumnName.MODIFY_FIELDS, header: 'InvUploadMaintenance.Column.modifyFields', sort: 'true', type: 'text' },
      { field: InvUploadColumnName.STATUS, header: 'InvUploadMaintenance.Column.status', sort: 'true', type: 'status' },
      { field: InvUploadColumnName.CREATED_BY, header: 'InvUploadMaintenance.Column.createdBy', sort: 'true', type: 'text' },
      { field: InvUploadColumnName.CREATED_AT, header: 'InvUploadMaintenance.Column.createdAt', sort: 'true', type: 'date' },
      { field: InvUploadColumnName.UPDATED_BY, header: 'InvUploadMaintenance.Column.updatedBy', sort: 'true', type: 'text' },
      { field: InvUploadColumnName.UPDATED_AT, header: 'InvUploadMaintenance.Column.updatedAt', sort: 'true', type: 'date' }
    ]
  }

  async ngOnInit(): Promise<void> {
    this.searchForm = this.fb.group({
      createDateStart: this.fb.control<string | null>(null),
      createDateEnd: this.fb.control<string | null>(null)
    });
    
  }

  uploadDateOnChange(event: DateRangeEvent): void {
    this.searchForm.patchValue({
      createDateStart: event.startDate ? event.startDate.toISOString() : null,
      createDateEnd: event.endDate ? event.endDate.toISOString() : null
    });
  }

  resetBtnClick(): void {
    this.searchForm.reset();
    this.uploadDate.ngOnInit();
  }

  searchBtnClick(): void {
    if (!this.searchForm.valid) {
      console.warn('Search form is invalid');
      return;
    }
    this.pageInfo.pageIndex = 0;
    this.table.setCurrentPage(0);
    this.performSearch();
  }

  onInvUploadFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    let fileList: FileList | null = element.files;
    
    if (fileList && fileList.length > 0) {
      this.selectedFile = fileList[0];
    }
  }

  uploadInvUploadFile(): void {
    if (!this.selectedFile) return;

    this.invUploadService.uploadExcel(this.selectedFile).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('Common.Toast.Success'));
        this.pageInfo.pageIndex = 0;
        this.table.setCurrentPage(0);
        this.performSearch();
        this.selectedFile = null;
      },
      error: (error: HttpErrorResponse) =>{
        this.toastErrorMessage(error.error.errorMsg);
      }
    });
  }

  performSearch() {
      const formValues = this.searchForm.value;
      const request: GetInvUploadRequest = {
        createDateStart: formValues.createDateStart ? new Date(formValues.createDateStart) : null,
        createDateEnd: formValues.createDateEnd ? new Date(formValues.createDateEnd) : null
      };

      this.isLoading = true;
      this.invUploadService.getInvUploads(request, this.pageInfo.pageIndex, this.pageInfo.pageSize, this.sortOrder)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response: PagedInvUploadResponse) => this.handleSearchSuccess(response),
        error: (error) => this.handleSearchError(error)
      });
  }

  onDownLoadExampleExcel(): void {
    this.isLoadingExampleExcel = true;
    this.invUploadService.downloadSampleExcel()
    .pipe(finalize(() => this.isLoadingExampleExcel = false))
    .subscribe({
      next: (file) => {
        FileDownloadUtil.download(file.blob, file.fileName);
      },
      error: (error: HttpErrorResponse) =>{
        this.toastErrorMessage(error.error.errorMsg);
      }
    });
  }

  onDownloadDetailExcel(invUploadNo: string): void {
    
    const request: DownloadInvUploadDetailRequest = {
      invUploadNo: invUploadNo
    };

    this.isLoadingDetailExcel.set(invUploadNo, true);
    this.invUploadService.downloadInvUploadDetailExcel(request)
      .pipe(finalize(() => this.isLoadingDetailExcel.set(invUploadNo, false)))
      .subscribe({
      next: (excelFile: ExcelFile) => {
        this.toastService.success(this.translateService.instant('Common.Toast.Success'));
        FileDownloadUtil.download(excelFile.blob, excelFile.fileName);
      },
      error: (error: HttpErrorResponse) =>{
        console.error('Failed to download inventory upload detail Excel:', error);
        this.toastErrorMessage(error.error.errorMsg);
      }
    });
  }

    /**
   * Handles successful search response
   * 
   * @param response - Paginated inventory upload data
   */
  private handleSearchSuccess(response: PagedInvUploadResponse): void {
    this.searchResults = this.invUploadMapper.toInvUploadHeaderRowData(response.content);
    this.pageInfo.totalRecords = response.totalElements;
    this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
    this.table.setPageInfo(this.pageInfo.totalRecords);
  }

    /**
   * Handles search operation errors
   * Logs error details and notifies user
   * 
   * @param error - The error object from the HTTP request
   */
  private handleSearchError(error: HttpErrorResponse): void {
    console.error('Search operation failed:', error);
    this.searchResults = [];
    this.pageInfo.totalRecords = 0;
    this.pageInfo.totalPages = 0;
    this.table.setPageInfo(this.pageInfo.totalRecords);
    this.toastErrorMessage(error.error.errorMsg);
  }

  tablePageChange($event: any) {
    this.pageInfo.pageSize = $event.rows;
    this.pageInfo.pageIndex = $event.first / this.pageInfo.pageSize;
    this.performSearch();
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.table.setCurrentPage(0);
    this.performSearch();
  }

  /**
   * Generates the i18n key for status enum values
   * Maps InvUploadStatusEnum values to their corresponding translation keys
   * 
   * Example: '0' -> 'InvUploadMaintenance.Status.0'
   * 
   * @param status - The status value from InvUploadStatusEnum
   * @returns The i18n translation key
   */
  getStatusTranslationKey(status: string): string {
    return `InvUploadMaintenance.Status.${status}`;
  }

  /**
   * Determines if detail excel download is allowed for the given status
   * Encapsulates business logic for download eligibility per SOLID principle
   * 
   * @param status - The inventory upload status to check
   * @returns true if the status allows download, false otherwise
   */
  isDetailDownloadAllowed(status: InvUploadStatusEnum): boolean {
    return this.DOWNLOADABLE_STATUSES.has(status);
  }
}
